import { makeAutoObservable, runInAction } from "mobx";
import { LayoutType } from "types/LayoutType";
import { RouterStoreConfig } from "../RouterStore";
import makeLayout from "../makeLayout";
import RootStore from "../../RootStore";

/**
 * Manages layout registration and selection.
 * Follows Single Responsibility Principle by focusing only on layout management.
 */
export class LayoutManager {
    // Observable state
    layoutsList: LayoutType[] = [];
    layouts: { [key: string]: LayoutType } = {};
    currentLayout: LayoutType | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * Registers layouts from a list of layout configurations
     * @param layoutsList - Array of layout configurations to register
     * @param config - Router configuration settings
     * @param rootStore - Reference to root store for layout lifecycle methods
     */
    async setLayouts(
        layoutsList: LayoutType[],
        config: RouterStoreConfig,
        rootStore: RootStore
    ): Promise<void> {
        const layouts = await this.layoutsFromList(layoutsList);

        runInAction(() => {
            this.layoutsList = layoutsList;
            this.layouts = layouts;
        });
    }

    /**
     * Converts layout list to layout object for fast lookup
     * @param layouts - Array of layout configurations
     * @returns Object with layout keys mapped to layout instances
     */
    private async layoutsFromList(layouts: LayoutType[]): Promise<{ [key: string]: LayoutType }> {
        const layoutsObject: { [key: string]: LayoutType } = {};

        for (const layout of layouts) {
            const layoutInstance = makeLayout(layout);
            // Cast the Layout instance back to LayoutType for compatibility
            layoutsObject[layoutInstance.key] = layoutInstance as unknown as LayoutType;
        }

        return layoutsObject;
    }

    /**
     * Sets the current active layout
     * @param layoutKey - The layout key to activate
     */
    setCurrentLayout(layoutKey: string): void {
        const layout = this.layouts[layoutKey];
        runInAction(() => {
            if (layout) {
                this.currentLayout = layout;
            } else {
                console.warn(`Layout "${layoutKey}" not found`);
            }
        });
    }

    /**
     * Finds a layout by its path
     * @param path - Layout path to search for
     * @returns Layout instance or null if not found
     */
    findLayoutByPath(path: string): LayoutType | null {
        for (const layout of Object.values(this.layouts)) {
            if (layout.path === path) {
                return layout;
            }
        }
        return null;
    }

    /**
     * Gets the default layout (typically the first one registered)
     * @returns Default layout instance or null if no layouts registered
     */
    getDefaultLayout(): LayoutType | null {
        return this.layoutsList.length > 0 ? this.layouts[this.layoutsList[0].name.replace(/ /g, '')] : null;
    }

    /**
     * Checks if a layout requires authentication
     * @param layoutKey - Layout key to check
     * @returns True if layout requires authentication
     */
    layoutRequiresAuth(layoutKey: string): boolean {
        const layout = this.layouts[layoutKey];
        return layout ? (layout.requiresAuth ?? true) : true;
    }

    /**
     * Checks if a layout requires admin privileges
     * @param layoutKey - Layout key to check
     * @returns True if layout requires admin access
     */
    layoutRequiresAdmin(layoutKey: string): boolean {
        const layout = this.layouts[layoutKey];
        return layout ? (layout.requiresAdmin ?? false) : false;
    }
}
